import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {IntersectionObserver | undefined} */
  #colorOrderObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#setupColorOrderProgressive();
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    this.#colorOrderObserver?.disconnect();
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Reveal manually ordered color cards in batches without changing their order.
   * Cards remain visible when JavaScript is unavailable.
   */
  #setupColorOrderProgressive() {
    if (!this.hasAttribute('color-order-progressive')) return;

    const { grid, viewMoreNext } = this.refs;
    if (!grid || !viewMoreNext) return;

    const cards = [...grid.querySelectorAll(':scope > li[ref="cards[]"]')];
    const batchSize = Number(this.getAttribute('color-order-batch-size')) || 16;
    let visibleCount = Math.min(batchSize, cards.length);

    for (const card of cards.slice(visibleCount)) {
      card.style.display = 'none';
    }

    if (visibleCount === cards.length) return;

    this.#colorOrderObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        const nextCount = Math.min(visibleCount + batchSize, cards.length);
        for (let index = visibleCount; index < nextCount; index++) {
          cards[index].style.removeProperty('display');
        }
        visibleCount = nextCount;

        if (visibleCount === cards.length) {
          this.#colorOrderObserver?.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    this.#colorOrderObserver.observe(viewMoreNext);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
